/**
 * MER-604: turn a manifest `widget_note` into something a customer can read.
 *
 * Sander asked on 2026-03-08 for "metric definition notes on widgets showing
 * the data source columns behind each number". The manifests have carried
 * those notes ever since (66 on DWS, 64 on Best Disposal, 104 in
 * templates/professional.yaml) and not one of them has ever rendered.
 *
 * They cannot render raw. The field doubles as our own build log, so the same
 * string that explains a metric also carries ticket ids, PR numbers and
 * internal spec revisions: "MER-465: completion_pct is the KPI_SUMMARY compute
 * (completed / attempted, the miss_rate complement, PR #453)". The definition
 * is what the customer asked for; "MER-465" and "PR #453" are ours.
 *
 * The rule this module follows: ONLY text that carried an internal reference
 * is edited. A note with no ticket in it reaches the reader verbatim, so a
 * two-word note like "Invoiced, uncollected" survives intact. A sentence we
 * had to strip a reference out of has to still stand on its own afterwards
 * (three real words or more) or it is dropped as provenance chatter, which is
 * what turns "Shipped on MER-125 PR" into nothing at all.
 *
 * Plain .mjs so node:test imports it with no build step (widgetDomain.mjs
 * precedent); widgetNote.d.mts types it for the TS callers.
 */

/**
 * Internal references. Each is removed from the text it appears in, and its
 * presence is also the signal that a sentence / parenthetical is provenance
 * rather than definition.
 */
const REF_SOURCES = [
  // "post-MER-184" / "pre-MER-320". Matched before the bare form so the
  // hyphenated prefix goes with it instead of stranding "post-".
  "\\b(?:post|pre)-MER-\\d+\\b",
  "\\bMER-\\d+\\b",
  // Internal spec revision ("SOTv9 Revenue Waterfall step 1").
  "\\bSOTv\\d+\\b",
  // MER-836: Zoho-era query and report ids ("CQT-23", "CQT-19a",
  // "RPT-FEL-02", "RPT-L1-1b-4"). Zoho is retired, so a customer reading
  // "CQT-23" is being shown the name of a system nobody runs any more.
  //
  // These are a guard, not the fix. The six notes that carried one were
  // rewritten to describe the measure, because stripping alone is not safe
  // here: "Sources from CQT-23 via RPT-FEL-02" decays to "Sources from",
  // which the three-word rule then drops, leaving a tile with no definition
  // rather than a bad one. What this pattern buys is that a SEVENTH cannot
  // reach a reader intact.
  "\\bCQT-\\d+[A-Za-z]?\\b",
  "\\bRPT-[A-Z0-9]+(?:-[A-Z0-9]+)*\\b",
  // "PR #130 + #131", "PR #453".
  "\\bPR\\s*#\\s*\\d+(?:\\s*\\+\\s*#\\s*\\d+)*",
  // A bare PR / issue number.
  "#\\d+",
];

const REF_ANY = new RegExp(REF_SOURCES.join("|"), "i");

/** True when `text` carries any internal reference. */
export function hasInternalRef(text) {
  return REF_ANY.test(String(text ?? ""));
}

function stripRefs(text) {
  let out = String(text);
  for (const source of REF_SOURCES) {
    out = out.replace(new RegExp(source, "gi"), " ");
  }
  // A "PR" left dangling by the number it referenced.
  out = out.replace(/\bPR\b(?=\s*(?:[.,;:)]|$))/g, " ");
  return out;
}

/**
 * Punctuation left behind by a removed reference. Deliberately narrow: it
 * repairs the seams, it does not rewrite prose.
 */
function tidy(text) {
  return (
    String(text)
      .replace(/\s+/g, " ")
      .replace(/\(\s+/g, "(")
      .replace(/\s+\)/g, ")")
      // Not "!" or "?": the notes carry comparison operators ("Ramp Division !=
      // operating division") and closing the space would read as punctuation.
      .replace(/\s+([.,;:])(?!=)/g, "$1")
      .replace(/[,;:+/]+\s*\)/g, ")")
      .replace(/\(\s*[,;:+/]+\s*/g, "(")
      .replace(/,\s*,/g, ",")
      .replace(/\+\s*\+/g, "+")
      .replace(/\.\s*\./g, ".")
      .replace(/^[\s:;,.+/-]+/, "")
      .trim()
  );
}

/** Real words, so "P4" and "AP" do not count as content. */
function contentWords(text) {
  return String(text).match(/\b[A-Za-z][A-Za-z_]{2,}\b/g) ?? [];
}

/**
 * Connector words a removed reference can leave hanging off the end of a
 * sentence ("Column added in MER-123" -> "Column added in" -> "Column added").
 */
const DANGLING =
  /\s+(?:on|in|from|per|via|by|and|plus|with|at|of|for|to|the|a|an|as|is|are|was|were)\s*([.!?]?)$/i;

function trimDangling(text) {
  let out = text;
  for (let i = 0; i < 4; i += 1) {
    const m = DANGLING.exec(out);
    if (!m) break;
    out = (out.slice(0, m.index) + (m[1] ?? "")).trim();
  }
  return out;
}

/**
 * Re-capitalise a sentence that lost its opening to a stripped reference
 * ("MER-320: tile aggregation routes..." -> "Tile aggregation routes...").
 *
 * Only ever applied to a plain lowercase word. Column names are the whole
 * point of these notes, so "completion_pct is the KPI_SUMMARY compute" and
 * "fuel_pct_of_revenue on Financial Health" keep their exact casing.
 */
function recapitalise(stripped, original) {
  if (/^[a-z]/.test(original)) return stripped;
  if (!/^[a-z][a-z]*(?=[\s,.:;]|$)/.test(stripped)) return stripped;
  return stripped[0].toUpperCase() + stripped.slice(1);
}

/**
 * Parentheticals are handled before sentences so "(MER-44 P1 + MER-77
 * surfaced 2026-05-16)" disappears whole instead of decaying into
 * "(P1 + surfaced 2026-05-16)", while "(completed / attempted, the
 * miss_rate complement, PR #453)" keeps its definition and loses only the PR.
 */
function scrubParentheticals(text) {
  return text.replace(/\(([^()]*)\)/g, (whole, inner) => {
    if (!hasInternalRef(inner)) return whole;
    const cleaned = tidy(stripRefs(inner)).replace(/[\s,;:+/-]+$/, "");
    if (contentWords(cleaned).length < 2) return " ";
    return `(${cleaned})`;
  });
}

/**
 * Client-facing form of a manifest widget_note.
 *
 * @param {unknown} raw the manifest value
 * @returns {string|null} the readable note, or null when nothing survives
 *   (an empty field, or a note that was pure build provenance)
 */
export function clientNote(raw) {
  if (typeof raw !== "string") return null;
  const flat = raw.replace(/\s+/g, " ").trim();
  if (!flat) return null;

  const scrubbed = scrubParentheticals(flat);
  const sentences = scrubbed.split(/(?<=[.!?])\s+/);
  const kept = [];
  for (const sentence of sentences) {
    // Untouched prose is never edited, only whitespace-tidied. This is what
    // keeps a legitimately short note ("Invoiced, uncollected") intact.
    if (!hasInternalRef(sentence)) {
      const plain = tidy(sentence);
      if (plain) kept.push(plain);
      continue;
    }
    const stripped = trimDangling(tidy(stripRefs(sentence)));
    if (contentWords(stripped).length < 3) continue;
    kept.push(recapitalise(stripped, sentence));
  }

  const out = kept.join(" ").trim();
  return out.length > 0 ? out : null;
}

/**
 * MER-558: the four things a reader needs about a number, in one panel.
 *
 * AJ's standing rule, 2026-08-11: every KPI ships with what it measures, how
 * it is measured, and the action an owner takes when it moves. MER-604 built
 * the disclosure and filled one of those three. The other two had nowhere to
 * live, so a tile could tell a reader where a number came from and still
 * leave them with no idea what to do about it.
 *
 * `measures` is deliberately the SAME string the caption renders
 * (`what_it_shows`). That is the point of the ticket's second ask: the face of
 * the card and the panel behind it come from one manifest field, so they
 * cannot drift apart the way a second hand-written summary would. It is
 * repeated inside the panel rather than dropped because a reader who has
 * opened the disclosure is reading the panel, not the card underneath it.
 *
 * Every part goes through clientNote(), so an internal reference cannot reach
 * a customer through a new field any more than it can through the old one.
 */
export const NOTE_SECTION_KEYS = Object.freeze(["measures", "note", "action", "good"]);

export const NOTE_SECTION_HEADINGS = Object.freeze({
  measures: "What this measures",
  note: "How this is measured",
  action: "What to do when it moves",
  good: "What good looks like",
});

/**
 * Build the panel's sections from a widget's copy fields.
 *
 * @param {{measures?: unknown, note?: unknown, action?: unknown, good?: unknown}} parts
 * @returns {{key: string, heading: string, body: string}[]} in reading order,
 *   with every empty or provenance-only field dropped.
 */
export function noteSections(parts) {
  const out = [];
  for (const key of NOTE_SECTION_KEYS) {
    const body = clientNote(parts?.[key]);
    if (body) out.push({ key, heading: NOTE_SECTION_HEADINGS[key], body });
  }
  return out;
}

/**
 * Whether a panel is worth a control at all.
 *
 * A tile whose only section is `measures` would open to a repeat of the
 * caption the reader can already see, so it gets no control. This keeps the
 * MER-604 behaviour exactly: the "i" appears where there is something behind
 * it, and nowhere else.
 */
export function hasTileInfo(sections) {
  return Array.isArray(sections) && sections.some((s) => s && s.key !== "measures");
}

/**
 * The control's accessible name, built from what the panel actually holds.
 *
 * A fixed "How X is measured" understated a panel that also says what to do,
 * and a fixed "About X" tells a screen reader nothing. Naming the sections
 * present is the only version that is true of every tile.
 */
export function noteControlLabel(label, sections) {
  const keys = new Set((Array.isArray(sections) ? sections : []).map((s) => s && s.key));
  const parts = [];
  if (keys.has("measures") || keys.has("note")) parts.push("how it is measured");
  if (keys.has("action")) parts.push("what to do when it moves");
  if (keys.has("good")) parts.push("what good looks like");
  return parts.length > 0 ? `${label}: ${parts.join(", ")}` : `About ${label}`;
}
